<template>
    <div class="flex justify-center mt-12">
      <div class="flex items-center space-x-1">
        <!-- 上一页 -->
        <nuxt-link 
          v-if="currentPage > 1" 
          :to="getPrePageUrl()" 
          class="px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-100 transition-colors">
          «
        </nuxt-link>
        <span v-else class="px-3 py-1 border border-gray-200 rounded-md text-gray-400 cursor-not-allowed">
          «
        </span>
  
        <!-- 页码 -->
        <template v-for="page in visiblePages" :key="page">
          <!-- 省略号 -->
          <span v-if="page === '...'" class="px-2 py-1 text-gray-500">...</span>
          
          <!-- 页码按钮 -->
          <nuxt-link 
            v-else
            :to="getPageUrl(page as number)" 
            class="px-3 py-1 border rounded-md transition-colors"
            :class="page === currentPage 
              ? 'border-green-600 bg-green-600 text-white' 
              : 'border-gray-300 text-gray-700 hover:border-gray-400 hover:bg-gray-50'">
            {{ page }}
          </nuxt-link>
        </template>
  
        <!-- 下一页 -->
        <nuxt-link 
          v-if="currentPage < totalPages" 
          :to="getNextPageUrl()" 
          class="px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-100 transition-colors">
          »
        </nuxt-link>
        <span v-else class="px-3 py-1 border border-gray-200 rounded-md text-gray-400 cursor-not-allowed">
          »
        </span>
      </div>
    </div>
  </template>
  
  <script setup lang="ts">
  interface Props {
    pageNum: number
    pageSize: number
    total: number
  }
  
  const props = defineProps<Props>()
  
  const route = useRoute()
  
  // 计算总页数
  const totalPages = computed(() => Math.ceil(props.total / props.pageSize))
  const currentPage = computed(() => props.pageNum)
  
  // 生成可见的页码数组（包含省略号）
  const visiblePages = computed(() => {
    const pages: (number | string)[] = []
    const total = totalPages.value
    const current = currentPage.value
    
    if (total <= 8) {
      // 总页数小于等于8，显示所有页码
      for (let i = 1; i <= total; i++) {
        pages.push(i)
      }
    } else {
      // 总页数大于8，需要缩略处理
      if (current <= 4) {
        // 当前页在前4页，显示前5页 + ... + 最后一页
        for (let i = 1; i <= 5; i++) {
          pages.push(i)
        }
        pages.push('...')
        pages.push(total)
      } else if (current >= total - 3) {
        // 当前页在后4页，显示第一页 + ... + 后5页
        pages.push(1)
        pages.push('...')
        for (let i = total - 4; i <= total; i++) {
          pages.push(i)
        }
      } else {
        // 当前页在中间，显示第一页 + ... + 当前页前后各2页 + ... + 最后一页
        pages.push(1)
        pages.push('...')
        for (let i = current - 2; i <= current + 2; i++) {
          pages.push(i)
        }
        pages.push('...')
        pages.push(total)
      }
    }
    
    return pages
  })
  

  const getPageUrl = (pageNum: number) => {
    let currentPath = route.path
    // 移除可能存在的 /page/{数字}.html 后缀，保留原始分类路径
    currentPath = currentPath.replace(/\/page\/\d+\.html$/, '.html')
    
    // 如果页码是1，返回不带查询参数的URL
    if (pageNum === 1) {
      return currentPath
    }
    
    // 其他页码，添加 ?page=pageNum 查询参数
    return `${currentPath}?page=${pageNum}`
  }


  const getPrePageUrl = () => {
    const prevPage = currentPage.value - 1
    return getPageUrl(prevPage)
  }

  const getNextPageUrl = () => {
    const nextPage = currentPage.value + 1
    return getPageUrl(nextPage)
  }

  </script>